/**
 * Per-user column ORDER and WIDTH — fleet filter contract v2 §13.2 RULE M2 and §13.4 RULE M8.
 *
 * 🔑 The boundary: anything that changes WHICH rows appear or in WHAT ORDER is list state and lives in
 * the URL; anything that changes how wide columns are or in what sequence they sit is PRESENTATION
 * and lives in a per-user preference, never in the URL. A colleague opening your link gets your
 * filters, sort and grouping — and their OWN column widths (C60). A resize or a move writes nothing to
 * the URL and adds no history entry (C59).
 *
 * Stored in localStorage keyed by grid AND user, so two people sharing a machine keep their own
 * layouts. Every access is wrapped: storage can be absent, full, or throw (a private window), and the
 * grid must render correctly without it — it simply falls back to the default layout.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ColumnPrefs {
  order: string[];
  widths: Record<string, number>;
}

export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 480;

export function prefsStorageKey(gridId: string, userKey: string | null | undefined): string {
  return `ptc-supervision:grid-prefs:${gridId}:${userKey || 'anonymous'}`;
}

/**
 * Reconcile a stored layout with the grid's current columns: unknown keys are dropped, columns the
 * stored layout does not know (added since) are appended in their default position order, widths are
 * clamped. A corrupt or foreign value is never trusted as-is.
 */
export function reconcilePrefs(stored: unknown, defaultOrder: readonly string[]): ColumnPrefs {
  const known = new Set(defaultOrder);
  const raw = (stored && typeof stored === 'object' ? stored : {}) as Partial<ColumnPrefs>;
  const order = Array.isArray(raw.order) ? raw.order.filter((k): k is string => typeof k === 'string' && known.has(k)) : [];
  const deduped = [...new Set(order)];
  for (const k of defaultOrder) if (!deduped.includes(k)) deduped.push(k);
  const widths: Record<string, number> = {};
  if (raw.widths && typeof raw.widths === 'object') {
    for (const [k, v] of Object.entries(raw.widths)) {
      if (known.has(k) && typeof v === 'number' && Number.isFinite(v)) {
        widths[k] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(v)));
      }
    }
  }
  return { order: deduped, widths };
}

function load(key: string, defaultOrder: readonly string[]): ColumnPrefs {
  try {
    const text = window.localStorage.getItem(key);
    return reconcilePrefs(text ? JSON.parse(text) : null, defaultOrder);
  } catch {
    return reconcilePrefs(null, defaultOrder);
  }
}

function save(key: string, prefs: ColumnPrefs): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Storage unavailable: the layout lives for this page view only. Never an error for the user.
  }
}

export interface UseColumnPrefs {
  order: string[];
  widths: Record<string, number>;
  canMoveLeft: (key: string) => boolean;
  canMoveRight: (key: string) => boolean;
  /** No-op on the leftmost column — never wraps to the other end (C65). */
  moveLeft: (key: string) => void;
  /** No-op on the rightmost column (C65). */
  moveRight: (key: string) => void;
  /** Set a width in px; `null` returns the column to its default width. */
  setWidth: (key: string, px: number | null) => void;
  resetLayout: () => void;
}

export function useColumnPrefs(gridId: string, userKey: string | null | undefined, defaultOrder: readonly string[]): UseColumnPrefs {
  const storageKey = prefsStorageKey(gridId, userKey);
  const defaultsSignature = defaultOrder.join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the signature, not the array identity
  const defaults = useMemo(() => [...defaultOrder], [defaultsSignature]);
  const [prefs, setPrefs] = useState<ColumnPrefs>(() => load(storageKey, defaults));

  // The user can resolve after mount (identity is async): reload that user's layout when the key moves.
  useEffect(() => {
    setPrefs(load(storageKey, defaults));
  }, [storageKey, defaults]);

  const update = useCallback(
    (fn: (p: ColumnPrefs) => ColumnPrefs) => {
      setPrefs((prev) => {
        const next = fn(prev);
        if (next !== prev) save(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const move = useCallback(
    (key: string, delta: -1 | 1) =>
      update((p) => {
        const i = p.order.indexOf(key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= p.order.length) return p;
        const order = [...p.order];
        [order[i], order[j]] = [order[j], order[i]];
        return { ...p, order };
      }),
    [update]
  );

  return {
    order: prefs.order,
    widths: prefs.widths,
    canMoveLeft: (key) => prefs.order.indexOf(key) > 0,
    canMoveRight: (key) => {
      const i = prefs.order.indexOf(key);
      return i >= 0 && i < prefs.order.length - 1;
    },
    moveLeft: (key) => move(key, -1),
    moveRight: (key) => move(key, 1),
    setWidth: (key, px) =>
      update((p) => {
        const widths = { ...p.widths };
        if (px === null) delete widths[key];
        else widths[key] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(px)));
        return { ...p, widths };
      }),
    resetLayout: () => update(() => reconcilePrefs(null, defaults)),
  };
}
